import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { api, type AcceleratorAgentOption, type AcceleratorHotspot } from '../../services/api';
import { portalPhotoUrl } from './AcceleratorProfileDialog';

export interface AcceleratorRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Props {
  open: boolean;
  posterKey: string;
  /** Existing area being edited; null for a freshly drawn one. */
  hotspot: AcceleratorHotspot | null;
  rect: AcceleratorRect | null;
  onClose: () => void;
  onSaved: (hotspot: AcceleratorHotspot) => void;
  onDeleted: (id: number) => void;
  /** Hide the form so the admin can drag a new rectangle for this area. */
  onRedraw: () => void;
}

const VIMEO_URL_PATTERN = /^https:\/\/([a-z0-9-]+\.)*(vimeo\.com|vimeocdn\.com|akamaized\.net)\//i;

function optionFromHotspot(hotspot: AcceleratorHotspot | null): AcceleratorAgentOption | null {
  if (!hotspot) return null;
  return { ...hotspot.agent, status: '' };
}

export default function AcceleratorHotspotForm({ open, posterKey, hotspot, rect, onClose, onSaved, onDeleted, onRedraw }: Props) {
  const [agent, setAgent] = useState<AcceleratorAgentOption | null>(null);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<AcceleratorAgentOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setAgent(optionFromHotspot(hotspot));
    setVideoUrl(hotspot?.videoUrl ?? '');
    setError(null);
  }, [open, hotspot]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2 || q === agent?.name) {
      setOptions([]);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const timer = window.setTimeout(() => {
      api
        .searchAcceleratorAgents(q)
        .then((rows) => !cancelled && setOptions(rows))
        .catch(() => !cancelled && setOptions([]))
        .finally(() => !cancelled && setSearching(false));
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, agent?.name]);

  const trimmedVideo = videoUrl.trim();
  const videoInvalid = !!trimmedVideo && !VIMEO_URL_PATTERN.test(trimmedVideo);

  const handleSave = async () => {
    if (!agent || !rect || videoInvalid) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await api.saveAcceleratorHotspot(hotspot?.id ?? null, {
        posterKey,
        ceaNumber: agent.ceaNumber,
        ...rect,
        videoUrl: trimmedVideo,
      });
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the area');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!hotspot || !window.confirm(`Remove the area for ${hotspot.agent.name || hotspot.agent.ceaNumber}?`)) return;
    setSaving(true);
    setError(null);
    try {
      await api.deleteAcceleratorHotspot(hotspot.id);
      onDeleted(hotspot.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete the area');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{hotspot ? 'Edit agent area' : 'Tag this area'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <Autocomplete
          value={agent}
          onChange={(_e, value) => setAgent(value)}
          inputValue={query}
          onInputChange={(_e, value) => setQuery(value)}
          options={agent && !options.some((o) => o.ceaNumber === agent.ceaNumber) ? [agent, ...options] : options}
          filterOptions={(x) => x}
          getOptionLabel={(option) => option.name || option.ceaNumber}
          isOptionEqualToValue={(a, b) => a.ceaNumber === b.ceaNumber}
          loading={searching}
          noOptionsText={query.trim().length < 2 ? 'Type a name or CEA number' : 'No agents found'}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.ceaNumber} sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
              <Avatar src={portalPhotoUrl(option.photo)} sx={{ width: 32, height: 32, fontSize: 13 }}>
                {(option.name || '?').substring(0, 2).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                  {option.name || '(no name)'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {[option.ceaNumber, option.designation, option.status].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Agent"
              placeholder="Search by name or CEA"
              autoFocus={!hotspot}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searching && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <TextField
          label="Vimeo video URL"
          placeholder="https://vimeo.com/123456789"
          value={videoUrl}
          onChange={(e) => setVideoUrl(e.target.value)}
          error={videoInvalid}
          helperText={
            videoInvalid
              ? 'Use a Vimeo link (vimeo.com, player.vimeo.com, or a Vimeo file / HLS link)'
              : 'Optional. Streams through our proxy, so the viewer never leaves the app.'
          }
          fullWidth
        />
        {rect && (
          <Typography variant="caption" color="text.secondary">
            Area: {rect.x.toFixed(1)}%, {rect.y.toFixed(1)}% · {rect.width.toFixed(1)}% × {rect.height.toFixed(1)}%
          </Typography>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {hotspot && (
          <>
            <Button color="error" onClick={handleDelete} disabled={saving} sx={{ mr: 'auto' }}>
              Delete
            </Button>
            <Button onClick={onRedraw} disabled={saving}>
              Redraw
            </Button>
          </>
        )}
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !agent || !rect || videoInvalid}>
          {saving ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
